import { toast } from "@/components/ui/use-toast";

interface MSPTask {
  id?: string;
  taskId?: string;
  name: string;
  startDate?: string;
  endDate?: string;
  duration: number; // working days
  progress?: number;
}

interface MSPProject {
  name: string;
  startDate?: string;
  endDate?: string;
  tasks?: MSPTask[];
}

interface TaskNameToUuidMap {
  [taskName: string]: string;
}

interface MSPAssignment {
  task_id: string;
  resource_id: string;
  name?: string | null;
  email?: string | null;
  role?: string | null;
  rate?: number | null;
  allocation_hrs?: string | null;
  allocated_percentage?: number | null;
}

interface MSPSubtask {
  id: string;
  task_id: string; // parent task name (DB convention)
  subtask_name: string;
  status?: string;
  estimated_hours?: number;
  actual_hours?: number;
  start_date?: string;
  end_date?: string;
  resource_id?: string;
  name?: string | null;
  email?: string | null;
  role?: string | null;
}

const HOURS_PER_DAY = 8;

const escapeXml = (val: any): string => {
  if (val == null) return '';
  return String(val)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
};

const toMSPDate = (d?: string | Date): string => {
  if (!d) return new Date().toISOString().split('.')[0];
  const dt = typeof d === 'string' ? new Date(d) : d;
  if (isNaN(dt.getTime())) return new Date().toISOString().split('.')[0];
  // MS Project XML format: YYYY-MM-DDTHH:MM:SS
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours() || 8)}:${pad(dt.getMinutes())}:${pad(dt.getSeconds())}`;
};

const toMSPDurationFromHours = (hours: number): string => {
  const h = Math.max(0, Math.round(hours));
  return `PT${h}H0M0S`;
};

const addBusinessDays = (start: Date, days: number): Date => {
  const d = new Date(start);
  let remaining = Math.max(0, days);
  while (remaining > 0) {
    d.setDate(d.getDate() + 1);
    const wd = d.getDay();
    if (wd !== 0 && wd !== 6) remaining--;
  }
  return d;
};

const subtaskProgressPct = (status?: string): number => {
  const s = (status || '').toLowerCase();
  if (s === 'done') return 100;
  if (s === 'in progress') return 50;
  return 0;
};

export const exportToMSProjectXML = (
  project: MSPProject,
  assignments: MSPAssignment[] = [],
  subtasks: MSPSubtask[] = [],
  taskNameToUuid: TaskNameToUuidMap = {}
) => {
  if (!project?.tasks?.length) {
    toast({ title: "Export Error", description: "No task data available for export", variant: "destructive" });
    return;
  }

  try {
    const projectStart = project.startDate ? new Date(project.startDate) : new Date();
    const projectFinish = project.endDate ? new Date(project.endDate) : addBusinessDays(projectStart, 30);

    // ---------- Build Tasks ----------
    let uid = 1;
    const taskUidByKey = new Map<string, number>(); // parent task name/id -> uid
    const tasksXml: string[] = [];

    // Project summary task (UID=0)
    tasksXml.push(`
    <Task>
      <UID>0</UID>
      <ID>0</ID>
      <Name>${escapeXml(project.name)}</Name>
      <Type>1</Type>
      <IsNull>0</IsNull>
      <OutlineLevel>0</OutlineLevel>
      <OutlineNumber>0</OutlineNumber>
      <Priority>500</Priority>
      <Start>${toMSPDate(projectStart)}</Start>
      <Finish>${toMSPDate(projectFinish)}</Finish>
      <Summary>1</Summary>
      <Critical>0</Critical>
      <Active>1</Active>
    </Task>`);

    let outlineIdx = 0;
    let prevParentUID: number | null = null;
    project.tasks.forEach((t, parentIdx) => {
      const parentUID = uid++;
      outlineIdx++;
      const wbs = `${outlineIdx}`;
      const candidateParentKeys = new Set(
        [t.id, t.taskId, t.name ? taskNameToUuid[t.name] : undefined, t.name]
          .filter((value): value is string => Boolean(value))
      );
      const childSubs = subtasks.filter((s) => candidateParentKeys.has(s.task_id));
      const isSummary = childSubs.length > 0 ? 1 : 0;

      // Derive parent start/finish to span subtasks when present
      let taskStart = t.startDate ? new Date(t.startDate) : projectStart;
      let taskFinish = t.endDate ? new Date(t.endDate) : addBusinessDays(taskStart, t.duration || 1);
      if (childSubs.length > 0) {
        const subStarts = childSubs.map(s => s.start_date ? new Date(s.start_date).getTime() : taskStart.getTime());
        const subFinishes = childSubs.map(s => s.end_date ? new Date(s.end_date).getTime() : taskFinish.getTime());
        taskStart = new Date(Math.min(taskStart.getTime(), ...subStarts));
        taskFinish = new Date(Math.max(taskFinish.getTime(), ...subFinishes));
      }
      const durationHrs = (t.duration || 0) * HOURS_PER_DAY;
      const progress = Math.max(0, Math.min(100, Math.round(t.progress || 0)));

      if (t.id) taskUidByKey.set(t.id, parentUID);
      if (t.taskId) taskUidByKey.set(t.taskId, parentUID);
      if (t.name) taskUidByKey.set(t.name, parentUID);

      const predLink = (prevParentUID !== null && parentIdx > 0) ? `<PredecessorLink>
        <PredecessorUID>${prevParentUID}</PredecessorUID>
        <Type>1</Type>
        <CrossProject>0</CrossProject>
        <LinkLag>0</LinkLag>
        <LagFormat>7</LagFormat>
      </PredecessorLink>` : '';

      tasksXml.push(`
    <Task>
      <UID>${parentUID}</UID>
      <ID>${parentUID}</ID>
      <Name>${escapeXml(t.name)}</Name>
      <Type>1</Type>
      <IsNull>0</IsNull>
      <WBS>${wbs}</WBS>
      <OutlineNumber>${wbs}</OutlineNumber>
      <OutlineLevel>1</OutlineLevel>
      <Priority>500</Priority>
      <Start>${toMSPDate(taskStart)}</Start>
      <Finish>${toMSPDate(taskFinish)}</Finish>
      <Duration>${toMSPDurationFromHours(durationHrs)}</Duration>
      <DurationFormat>7</DurationFormat>
      <Work>${toMSPDurationFromHours(durationHrs)}</Work>
      <Manual>0</Manual>
      <Summary>${isSummary}</Summary>
      <Active>1</Active>
      <Critical>0</Critical>
      <PercentComplete>${progress}</PercentComplete>
      <PercentWorkComplete>${progress}</PercentWorkComplete>
      <ConstraintType>0</ConstraintType>
      <FixedCostAccrual>3</FixedCostAccrual>
      ${predLink}
    </Task>`);
      prevParentUID = parentUID;

      // Subtasks
      childSubs.forEach((s, sIdx) => {
        const subUID = uid++;
        const subWbs = `${outlineIdx}.${sIdx + 1}`;
        const subStart = s.start_date ? new Date(s.start_date) : taskStart;
        const subFinish = s.end_date ? new Date(s.end_date) : taskFinish;
        const subHrs = Math.max(0, Number(s.estimated_hours) || 0);
        const subProgress = subtaskProgressPct(s.status);
        taskUidByKey.set(`sub:${s.id}`, subUID);
        tasksXml.push(`
    <Task>
      <UID>${subUID}</UID>
      <ID>${subUID}</ID>
      <Name>${escapeXml(s.subtask_name)}</Name>
      <Type>1</Type>
      <IsNull>0</IsNull>
      <WBS>${subWbs}</WBS>
      <OutlineNumber>${subWbs}</OutlineNumber>
      <OutlineLevel>2</OutlineLevel>
      <Priority>500</Priority>
      <Start>${toMSPDate(subStart)}</Start>
      <Finish>${toMSPDate(subFinish)}</Finish>
      <Duration>${toMSPDurationFromHours(subHrs)}</Duration>
      <DurationFormat>7</DurationFormat>
      <Work>${toMSPDurationFromHours(subHrs)}</Work>
      <Manual>0</Manual>
      <Summary>0</Summary>
      <Active>1</Active>
      <Critical>0</Critical>
      <PercentComplete>${subProgress}</PercentComplete>
      <PercentWorkComplete>${subProgress}</PercentWorkComplete>
      <ConstraintType>0</ConstraintType>
      <FixedCostAccrual>3</FixedCostAccrual>
    </Task>`);
      });
    });

    // ---------- Resources ----------
    const rsrcUidByUuid = new Map<string, number>();
    const rsrcNameByUuid = new Map<string, string>();
    const rsrcRateByUuid = new Map<string, number>();
    let rsrcUid = 1;
    const registerRsrc = (uuid?: string | null, displayName?: string | null, rate?: number | null) => {
      if (!uuid) return;
      if (rsrcUidByUuid.has(uuid)) return;
      rsrcUidByUuid.set(uuid, rsrcUid++);
      rsrcNameByUuid.set(uuid, displayName || `Resource ${rsrcUid}`);
      if (rate != null) rsrcRateByUuid.set(uuid, Number(rate) || 0);
    };
    assignments.forEach(a => registerRsrc(a.resource_id, a.name || a.email || a.role, a.rate));
    subtasks.forEach(s => registerRsrc(s.resource_id, s.name || s.email || s.role));

    const resourcesXml: string[] = [];
    resourcesXml.push(`
    <Resource>
      <UID>0</UID>
      <ID>0</ID>
      <Name>Unassigned</Name>
      <Type>1</Type>
      <IsNull>0</IsNull>
    </Resource>`);
    rsrcUidByUuid.forEach((rid, uuid) => {
      const name = rsrcNameByUuid.get(uuid) || `Resource ${rid}`;
      const rate = rsrcRateByUuid.get(uuid) || 0;
      resourcesXml.push(`
    <Resource>
      <UID>${rid}</UID>
      <ID>${rid}</ID>
      <Name>${escapeXml(name)}</Name>
      <Type>1</Type>
      <IsNull>0</IsNull>
      <MaxUnits>1</MaxUnits>
      <StandardRate>${rate}</StandardRate>
      <StandardRateFormat>2</StandardRateFormat>
      <Cost>0</Cost>
      <OvertimeRate>0</OvertimeRate>
      <OvertimeRateFormat>2</OvertimeRateFormat>
      <AccrueAt>3</AccrueAt>
      <CalendarUID>-1</CalendarUID>
    </Resource>`);
    });

    // ---------- Assignments ----------
    const assignmentsXml: string[] = [];
    let assignUid = 1;
    assignments.forEach(a => {
      const taskUID = taskUidByKey.get(a.task_id);
      const rsrcUIDv = a.resource_id ? rsrcUidByUuid.get(a.resource_id) : undefined;
      if (!taskUID || rsrcUIDv == null) return;
      const allocHrs = a.allocation_hrs != null ? Number(a.allocation_hrs) : NaN;
      const allocPct = Number(a.allocated_percentage);
      // Find the parent task to get duration in hours
      const parentTask = project.tasks!.find(t => t.id === a.task_id || t.taskId === a.task_id || t.name === a.task_id);
      const taskHrs = ((parentTask?.duration) || 0) * HOURS_PER_DAY;
      const workHrs = !isNaN(allocHrs) && allocHrs > 0
        ? allocHrs
        : taskHrs * ((isNaN(allocPct) ? 100 : allocPct) / 100);

      assignmentsXml.push(`
    <Assignment>
      <UID>${assignUid++}</UID>
      <TaskUID>${taskUID}</TaskUID>
      <ResourceUID>${rsrcUIDv}</ResourceUID>
      <Units>${isNaN(allocPct) ? 1 : allocPct / 100}</Units>
      <Work>${toMSPDurationFromHours(workHrs)}</Work>
      <RegularWork>${toMSPDurationFromHours(workHrs)}</RegularWork>
      <RemainingWork>${toMSPDurationFromHours(workHrs)}</RemainingWork>
      <Start>${toMSPDate(parentTask?.startDate)}</Start>
      <Finish>${toMSPDate(parentTask?.endDate)}</Finish>
    </Assignment>`);
    });
    subtasks.forEach(s => {
      const taskUID = taskUidByKey.get(`sub:${s.id}`);
      const rsrcUIDv = s.resource_id ? rsrcUidByUuid.get(s.resource_id) : undefined;
      if (!taskUID || rsrcUIDv == null) return;
      const workHrs = Math.max(0, Number(s.estimated_hours) || 0);
      assignmentsXml.push(`
    <Assignment>
      <UID>${assignUid++}</UID>
      <TaskUID>${taskUID}</TaskUID>
      <ResourceUID>${rsrcUIDv}</ResourceUID>
      <Units>1</Units>
      <Work>${toMSPDurationFromHours(workHrs)}</Work>
      <RegularWork>${toMSPDurationFromHours(workHrs)}</RegularWork>
      <RemainingWork>${toMSPDurationFromHours(workHrs)}</RemainingWork>
      <Start>${toMSPDate(s.start_date)}</Start>
      <Finish>${toMSPDate(s.end_date)}</Finish>
    </Assignment>`);
    });

    // ---------- Build XML ----------
    const xml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Project xmlns="http://schemas.microsoft.com/project">
  <SaveVersion>14</SaveVersion>
  <Name>${escapeXml(project.name)}.xml</Name>
  <Title>${escapeXml(project.name)}</Title>
  <Manager></Manager>
  <ScheduleFromStart>1</ScheduleFromStart>
  <StartDate>${toMSPDate(projectStart)}</StartDate>
  <FinishDate>${toMSPDate(projectFinish)}</FinishDate>
  <FYStartDate>1</FYStartDate>
  <CriticalSlackLimit>0</CriticalSlackLimit>
  <CurrencyDigits>2</CurrencyDigits>
  <CurrencySymbol>$</CurrencySymbol>
  <CurrencyCode>USD</CurrencyCode>
  <CurrencySymbolPosition>0</CurrencySymbolPosition>
  <CalendarUID>1</CalendarUID>
  <DefaultStartTime>08:00:00</DefaultStartTime>
  <DefaultFinishTime>17:00:00</DefaultFinishTime>
  <MinutesPerDay>480</MinutesPerDay>
  <MinutesPerWeek>2400</MinutesPerWeek>
  <DaysPerMonth>20</DaysPerMonth>
  <DefaultTaskType>0</DefaultTaskType>
  <DefaultFixedCostAccrual>3</DefaultFixedCostAccrual>
  <DefaultStandardRate>0</DefaultStandardRate>
  <DefaultOvertimeRate>0</DefaultOvertimeRate>
  <DurationFormat>7</DurationFormat>
  <WorkFormat>2</WorkFormat>
  <EditableActualCosts>0</EditableActualCosts>
  <HonorConstraints>1</HonorConstraints>
  <InsertedProjectsLikeSummary>1</InsertedProjectsLikeSummary>
  <MultipleCriticalPaths>0</MultipleCriticalPaths>
  <NewTasksEffortDriven>0</NewTasksEffortDriven>
  <NewTasksEstimated>1</NewTasksEstimated>
  <SplitsInProgressTasks>1</SplitsInProgressTasks>
  <SpreadActualCost>0</SpreadActualCost>
  <SpreadPercentComplete>0</SpreadPercentComplete>
  <TaskUpdatesResource>1</TaskUpdatesResource>
  <FiscalYearStart>0</FiscalYearStart>
  <WeekStartDay>1</WeekStartDay>
  <MoveCompletedEndsBack>0</MoveCompletedEndsBack>
  <MoveRemainingStartsBack>0</MoveRemainingStartsBack>
  <MoveRemainingStartsForward>0</MoveRemainingStartsForward>
  <MoveCompletedEndsForward>0</MoveCompletedEndsForward>
  <BaselineForEarnedValue>0</BaselineForEarnedValue>
  <AutoAddNewResourcesAndTasks>1</AutoAddNewResourcesAndTasks>
  <CurrentDate>${toMSPDate(new Date())}</CurrentDate>
  <MicrosoftProjectServerURL>0</MicrosoftProjectServerURL>
  <Autolink>1</Autolink>
  <NewTaskStartDate>0</NewTaskStartDate>
  <DefaultTaskEVMethod>0</DefaultTaskEVMethod>
  <ProjectExternallyEdited>0</ProjectExternallyEdited>
  <ExtendedCreationDate>${toMSPDate(new Date())}</ExtendedCreationDate>
  <ActualsInSync>0</ActualsInSync>
  <RemoveFileProperties>0</RemoveFileProperties>
  <AdminProject>0</AdminProject>
  <Calendars>
    <Calendar>
      <UID>1</UID>
      <Name>Standard</Name>
      <IsBaseCalendar>1</IsBaseCalendar>
      <BaseCalendarUID>-1</BaseCalendarUID>
      <WeekDays>
        <WeekDay><DayType>1</DayType><DayWorking>0</DayWorking></WeekDay>
        <WeekDay><DayType>2</DayType><DayWorking>1</DayWorking><WorkingTimes><WorkingTime><FromTime>08:00:00</FromTime><ToTime>12:00:00</ToTime></WorkingTime><WorkingTime><FromTime>13:00:00</FromTime><ToTime>17:00:00</ToTime></WorkingTime></WorkingTimes></WeekDay>
        <WeekDay><DayType>3</DayType><DayWorking>1</DayWorking><WorkingTimes><WorkingTime><FromTime>08:00:00</FromTime><ToTime>12:00:00</ToTime></WorkingTime><WorkingTime><FromTime>13:00:00</FromTime><ToTime>17:00:00</ToTime></WorkingTime></WorkingTimes></WeekDay>
        <WeekDay><DayType>4</DayType><DayWorking>1</DayWorking><WorkingTimes><WorkingTime><FromTime>08:00:00</FromTime><ToTime>12:00:00</ToTime></WorkingTime><WorkingTime><FromTime>13:00:00</FromTime><ToTime>17:00:00</ToTime></WorkingTime></WorkingTimes></WeekDay>
        <WeekDay><DayType>5</DayType><DayWorking>1</DayWorking><WorkingTimes><WorkingTime><FromTime>08:00:00</FromTime><ToTime>12:00:00</ToTime></WorkingTime><WorkingTime><FromTime>13:00:00</FromTime><ToTime>17:00:00</ToTime></WorkingTime></WorkingTimes></WeekDay>
        <WeekDay><DayType>6</DayType><DayWorking>1</DayWorking><WorkingTimes><WorkingTime><FromTime>08:00:00</FromTime><ToTime>12:00:00</ToTime></WorkingTime><WorkingTime><FromTime>13:00:00</FromTime><ToTime>17:00:00</ToTime></WorkingTime></WorkingTimes></WeekDay>
        <WeekDay><DayType>7</DayType><DayWorking>0</DayWorking></WeekDay>
      </WeekDays>
    </Calendar>
  </Calendars>
  <Tasks>${tasksXml.join('')}
  </Tasks>
  <Resources>${resourcesXml.join('')}
  </Resources>
  <Assignments>${assignmentsXml.join('')}
  </Assignments>
</Project>`;

    const blob = new Blob([xml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const dateStr = new Date().toISOString().split('T')[0];
    a.download = `${project.name}_MSProject_Export_${dateStr}.xml`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast({
      title: "Export Successful",
      description: `MS Project XML exported (${project.tasks.length} tasks, ${subtasks.length} subtasks). Open it in Microsoft Project via File > Open.`,
    });
  } catch (error) {
    console.error('MS Project export error:', error);
    toast({ title: "Export Error", description: "Failed to export MS Project XML", variant: "destructive" });
  }
};
